<template>
  <div class="fiscal-selector-container mb-24 text-center">
    <div class="fiscal-group-pill">
      <a-radio-group :value="innerSelectedYear" class="fiscal-year-radio-group font-web" @change="onYearChange">
        <a-radio-button v-for="year in fiscalYearList" :key="year" :value="year" class="fiscal-pill-btn">
          <span class="btn-year-prefix">ปีงบประมาณ</span>
          <span class="btn-year-number">{{ year }}</span>
          <span v-if="year === currentFiscalYear" class="current-indicator-dot" title="ปีปัจจุบัน"></span>
        </a-radio-button>
      </a-radio-group>
    </div>

    <div class="fiscal-period-wrapper mt-10">
      <div class="period-badge font-web">
        <a-icon type="calendar" class="period-icon mr-10" />
        <span class="period-text">
          {{ formatFiscalPeriod(innerSelectedYear) }}
        </span>
        <a-tag v-if="isSelectedCurrentYear" color="orange" class="ml-8 current-year-tag font-web">
          ปีปัจจุบัน (ยังไม่มีข้อมูล)
        </a-tag>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FiscalYearSelector",
  model: {
    prop: "value",
    event: "change",
  },
  props: {
    value: {
      type: Number,
      default: null,
    },
  },
  data() {
    const currentFY = this.calculateCurrentFiscalYear();
    const defaultFY = currentFY - 1;

    return {
      currentFiscalYear: currentFY,
      innerSelectedYear: this.value || defaultFY,
    };
  },
  computed: {
    // 5 fiscal years ending at current fiscal year
    fiscalYearList() {
      const list = [];
      for (let i = 4; i >= 0; i--) {
        list.push(this.currentFiscalYear - i);
      }
      return list;
    },
    isSelectedCurrentYear() {
      return this.innerSelectedYear === this.currentFiscalYear;
    },
  },
  watch: {
    value(newVal) {
      if (newVal && newVal !== this.innerSelectedYear) {
        this.innerSelectedYear = newVal;
      }
    },
  },
  created() {
    // Emit initial default value if not provided by parent
    if (!this.value) {
      this.$emit("change", this.innerSelectedYear);
      this.$emit("input", this.innerSelectedYear);
    }
  },
  methods: {
    calculateCurrentFiscalYear(refDate = new Date()) {
      const date = refDate instanceof Date ? refDate : new Date(refDate);
      const ceYear = date.getFullYear();
      const month = date.getMonth() + 1; // 1-12
      // Fiscal year starts in October: Month 10, 11, 12 belong to next CE year
      const fiscalCeYear = month >= 10 ? ceYear + 1 : ceYear;
      return fiscalCeYear + 543; // BE Year
    },
    formatFiscalPeriod(year) {
      if (!year) return "";
      const startYear = year - 1;
      return `ข้อมูล 1 ตุลาคม ${startYear} - 30 กันยายน ${year}`;
    },
    onYearChange(e) {
      const year = e.target.value;
      this.innerSelectedYear = year;
      this.$emit("change", year);
      this.$emit("input", year);
    },
  },
};
</script>

<style scoped>
.fiscal-selector-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
}

/* Outer Pill Container with Glassmorphism / Card style */
.fiscal-group-pill {
  display: inline-flex;
  padding: 5px;
  background: #f1f3f9;
  border-radius: 30px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.03);
  max-width: 100%;
  overflow-x: auto;
}

.fiscal-year-radio-group {
  display: inline-flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  background: transparent;
  border: none;
}

/* Override Ant Design radio-button default styles for modern pills */
.fiscal-year-radio-group ::v-deep .ant-radio-button-wrapper {
  border: none !important;
  background: transparent !important;
  border-radius: 24px !important;
  height: 38px !important;
  line-height: 38px !important;
  padding: 0 16px !important;
  font-size: 13.5px !important;
  font-weight: 600 !important;
  color: #555b6d !important;
  box-shadow: none !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  position: relative !important;
}

.fiscal-year-radio-group ::v-deep .ant-radio-button-wrapper:not(:first-child)::before {
  display: none !important;
}

.fiscal-year-radio-group ::v-deep .ant-radio-button-wrapper:hover {
  color: #350b4f !important;
  background: rgba(255, 255, 255, 0.6) !important;
}

.fiscal-year-radio-group ::v-deep .ant-radio-button-wrapper-checked {
  background: linear-gradient(135deg, #4c116d 0%, #2f0747 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(53, 11, 79, 0.35) !important;
  transform: translateY(-1px);
}

.fiscal-year-radio-group ::v-deep .ant-radio-button-wrapper-checked .btn-year-prefix {
  color: rgba(255, 255, 255, 0.85) !important;
}

.fiscal-year-radio-group ::v-deep .ant-radio-button-wrapper-checked .btn-year-number {
  color: #ffc624 !important;
  font-weight: 800 !important;
}

.btn-year-prefix {
  margin-right: 4px;
  font-size: 13px;
  font-weight: 500;
  transition: color 0.2s;
}

.btn-year-number {
  font-size: 14px;
  font-weight: 700;
  transition: color 0.2s;
}

.current-indicator-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #ff9800;
  margin-left: 6px;
  display: inline-block;
}

/* Fiscal Period Badge */
.fiscal-period-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.period-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  background-color: #fafbfe;
  border: 1px solid #e8ecf4;
  border-radius: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.period-icon {
  color: #5832e5;
  font-size: 13px;
}

.period-text {
  font-size: 12.5px;
  color: #636b7e;
  font-weight: 500;
}

.current-year-tag {
  border-radius: 12px;
  font-size: 11px;
  padding: 0 8px;
  height: 20px;
  line-height: 18px;
  border: none;
}
</style>
